import { Controller } from "@hotwired/stimulus"
import { showNavigationEdgeNotice } from "../lib/navigation_edge_notice"

export default class extends Controller {
  static values = {
    currentProcess: String,
    autoAdvance: Boolean,
  }

  static targets = ["titleContainer", "nextButton", "loadingButton", "prevButton", "backToIndexButton", "prevTooltip", "nextTooltip", "autoAdvanceIndicator", "positionLabel"]

  connect() {
    this.setBackToListHref()
    this.updateNextLink()
    this.updatePrevLink()

    // Links from another workflow can request an attachment to open once.
    const url = new URL(window.location.href)
    const attachmentId = url.searchParams.get('attachment_id')

    if (attachmentId) {
      const attachmentLink = document.querySelector(`:is(.paperwork-attachment-modal-link, .file-tile)[data-paperwork-attachment-id="${attachmentId}"]`)
      if (attachmentLink) {
        // Consume the request before opening it. Otherwise a later page
        // restore reconnects this controller and opens the document again.
        url.searchParams.delete('attachment_id')
        window.history.replaceState(window.history.state, '', url)
        const hasModal = ['modal_attachment', 'modal_task', 'modal_contact'].some(param => url.searchParams.get(param))
        if (!hasModal) attachmentLink.click()
      }
    }

    // Bind the handler once and store the reference
    this.boundHandleNumberKey = this.handleNumberKey.bind(this)
    
    // Add keyboard shortcuts for number keys 1-9
    document.addEventListener('keydown', this.boundHandleNumberKey)
  }

  disconnect() {
    // Clean up keyboard event listener using the stored reference
    if (this.boundHandleNumberKey) {
      document.removeEventListener('keydown', this.boundHandleNumberKey)
    }
  }

  handleNumberKey(event) {
    // Handle question mark to show help
    if (event.key === '?' && !this.isInInputField(event.target)) {
      event.preventDefault()
      this.showKeyboardShortcutsHelp()
      return
    }

    // Handle Alt key combinations first
    if (event.altKey) {
      // Alt + A: Focus and open the assign select
      if (event.code.toLowerCase() === 'keya') {
        event.preventDefault()
        this.focusAssignSelect()
        return
      }
      
      // Alt + S: Focus and open the state select
      if (event.code.toLowerCase() === 'keys') {
        event.preventDefault()
        this.focusStateSelect()
        return
      }

      // Alt + Shift combinations for direct state changes
      if (event.shiftKey) {
        // Alt + Shift + O: Set to Open
        if (event.code.toLowerCase() === 'keyo') {
          event.preventDefault()
          this.setProcessState('open')
          return
        }
        
        // Alt + Shift + H: Set to On Hold
        if (event.code.toLowerCase() === 'keyh') {
          event.preventDefault()
          this.setProcessState('on_hold')
          return
        }
        
        // Alt + Shift + C: Set to Completed
        if (event.code.toLowerCase() === 'keyc') {
          event.preventDefault()
          this.setProcessState('completed')
          return
        }
        
        // Alt + Shift + X: Set to Cancelled
        if (event.code.toLowerCase() === 'keyx') {
          event.preventDefault()
          this.setProcessState('cancelled')
          return
        }
      }
    }

    // Only handle number keys 1-9
    if (!/^[1-9]$/.test(event.key)) return

    // Don't handle if we're in a text field or other input element
    const activeElement = document.activeElement;
    const inputElements = ['INPUT', 'TEXTAREA', 'SELECT'];
    if (inputElements.includes(activeElement.tagName)) return;

    // Get all attachment links in the context panel
    const attachmentLinks = document.querySelectorAll('.process-context :is(.paperwork-attachment-modal-link, .file-tile)')
    const index = parseInt(event.key) - 1 // Convert 1-9 to 0-8

    // If there's an attachment at this index, click it
    if (attachmentLinks[index]) {
      attachmentLinks[index].click()
    }
  }

  focusAssignSelect() {
    const assignSelect = document.querySelector('select[name="paperwork_process[assigned_user_id]"]')
    if (assignSelect) {
      // On mobile, ensure context is visible first
      this.ensureContextVisible()
      
      // Focus and open the select
      assignSelect.focus()
      assignSelect.click()
    }
  }

  focusStateSelect() {
    const stateSelect = document.querySelector('select[name="paperwork_process[state]"]')
    if (stateSelect) {
      // On mobile, ensure context is visible first
      this.ensureContextVisible()
      
      // Focus and open the select
      stateSelect.focus()
      stateSelect.click()
    }
  }

  setProcessState(state) {
    // Prevent rapid multiple submissions
    if (this.isSubmitting) {
      return
    }
    
    const stateSelect = document.querySelector('select[name="paperwork_process[state]"]')
    if (stateSelect && stateSelect.value !== state) {
      this.isSubmitting = true
      
      // Set the value and trigger the change event to submit
      stateSelect.value = state
      stateSelect.dispatchEvent(new Event('change', { bubbles: true }))
      
      // Brief visual feedback
      stateSelect.style.backgroundColor = 'var(--color-brand-accent)'
      stateSelect.style.color = 'white'
      setTimeout(() => {
        stateSelect.style.backgroundColor = ''
        stateSelect.style.color = ''
        this.isSubmitting = false
      }, 1000)
    }
  }

  ensureContextVisible() {
    // On mobile (screen width < 1280px), check if context is hidden and toggle it
    if (window.innerWidth < 1280) {
      const contextContent = document.querySelector('[data-toggle-context-target="content"]')
      if (contextContent && contextContent.dataset.visible !== "true") {
        const toggleButton = document.querySelector('[data-action*="toggle-context#toggle"]')
        if (toggleButton) {
          toggleButton.click()
        }
      }
    }
  }

  getAllProcesses() {
    try {
      const processes = JSON.parse(localStorage.getItem("paperworkProcessReferences"));
      return processes || [];
    } catch (error) {
      console.error("Error parsing processes from localStorage:", error);
      return [];
    }
  }

  isInInputField(element) {
    const tagName = element.tagName.toLowerCase()
    return tagName === "input" || tagName === "textarea" || tagName === "select" || element.isContentEditable
  }

  showKeyboardShortcutsHelp() {
    // Remove existing help modal if present
    const existingModal = document.getElementById('keyboard-shortcuts-help')
    if (existingModal) {
      existingModal.remove()
    }

    // Create modal HTML
    const modalHTML = `
      <dialog id="keyboard-shortcuts-help" class="m-auto rounded-lg backdrop:bg-black/80 backdrop:cursor-pointer max-w-lg">
        <div class="bg-brand-bg p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold">Keyboard shortcuts</h2>
            <button class="btn btn-sm btn-ghost btn-circle" onclick="this.closest('dialog').close(); this.closest('dialog').remove()">
              <i class="ti ti-x"></i>
            </button>
          </div>
          
          <div class="space-y-4 text-sm">
            <div class="flex justify-between gap-2">
              <span>Focus Assignment</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">A</kbd></div>
            </div>
            <div class="flex justify-between gap-2">
              <span>Focus State</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">S</kbd></div>
            </div>
            <hr class="border-gray-200">
            <div class="flex justify-between gap-2">
              <span>Set to Open</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">Shift</kbd> + <kbd class="kbd kbd-sm">O</kbd></div>
            </div>
            <div class="flex justify-between gap-2">
              <span>Set to On Hold</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">Shift</kbd> + <kbd class="kbd kbd-sm">H</kbd></div>
            </div>
            <div class="flex justify-between gap-2">
              <span>Set to Completed</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">Shift</kbd> + <kbd class="kbd kbd-sm">C</kbd></div>
            </div>
            <div class="flex justify-between gap-2">
              <span>Set to Cancelled</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">Shift</kbd> + <kbd class="kbd kbd-sm">X</kbd></div>
            </div>
            <hr class="border-gray-200">
            <div class="flex justify-between gap-2">
              <span>Previous Process</span>
              <kbd class="kbd kbd-sm">←</kbd>
            </div>
            <div class="flex justify-between gap-2">
              <span>Next Process</span>
              <kbd class="kbd kbd-sm">→</kbd>
            </div>
            <div class="flex justify-between gap-2">
              <span>Open Attachments</span>
              <div><kbd class="kbd kbd-sm">1</kbd> - <kbd class="kbd kbd-sm">9</kbd></div>
            </div>
          </div>
        </div>
      </dialog>
    `

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML)
    
    // Show the dialog
    const modal = document.getElementById('keyboard-shortcuts-help')
    modal.showModal()
    
    // Handle escape key and backdrop click
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        modal.close()
        modal.remove()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    
    const handleBackdropClick = (e) => {
      const rect = modal.getBoundingClientRect()
      const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                         rect.left <= e.clientX && e.clientX <= rect.left + rect.width)
      if (!isInDialog) {
        modal.close()
        modal.remove()
        document.removeEventListener('keydown', handleEscape)
        modal.removeEventListener('click', handleBackdropClick)
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    modal.addEventListener('click', handleBackdropClick)
  }

  isInTriage() {
    const processes = this.getAllProcesses();
    if (!processes.length) return false;
    const currentIndex = processes.findIndex(process => process === this.currentProcessValue);
    return currentIndex !== -1;
  }

  // Point the persistent "back to list" button at the list the user came from
  // (with its search/filters preserved), falling back to the server-rendered
  // index path when there's no stored list URL.
  setBackToListHref() {
    if (!this.hasBackToIndexButtonTarget) return;
    try {
      const listUrl = localStorage.getItem("paperworkProcessListUrl");
      if (listUrl) {
        this.backToIndexButtonTarget.href = listUrl;
      }
    } catch (error) {
      console.error("Error reading process list URL from localStorage:", error);
    }
  }

  updatePrevLink() {
    // The left arrow is purely "previous": it shows only when there's an
    // earlier process in the review queue. "Back to list" is a separate,
    // always-visible list button, so the arrow never doubles as back.
    const processes = this.getAllProcesses();
    const currentIndex = processes.length
      ? processes.findIndex(process => process === this.currentProcessValue)
      : -1;

    if (currentIndex > 0) {
      this.showPrevLink(processes[currentIndex - 1], currentIndex, processes.length);
    } else {
      this.hidePrevLink();
    }
  }

  hidePrevLink() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.setAttribute("aria-label", "Previous process");
      this.prevButtonTarget.href = "#";
      this.prevButtonTarget.setAttribute("aria-disabled", "true");
      this.prevButtonTarget.setAttribute("tabindex", "-1");
      this.prevButtonTarget.classList.add("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasPrevTooltipTarget) {
      this.prevTooltipTarget.dataset.tip = "No previous process";
    }
  }

  showPrevLink(prevProcessId, currentPosition, total) {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.href = `/processes/${prevProcessId}`;
      const label = `Previous workflow in review queue (${currentPosition}/${total})`;
      this.prevButtonTarget.setAttribute("aria-label", label);
      this.prevButtonTarget.setAttribute("aria-disabled", "false");
      this.prevButtonTarget.removeAttribute("tabindex");
      this.prevButtonTarget.classList.remove("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasPrevTooltipTarget) {
      this.prevTooltipTarget.dataset.tip = "Previous process";
    }
  }

  shouldHandleNavigation() {
    // Don't navigate if user is focused on an input, button, or other interactive element
    const activeElement = document.activeElement;
    const interactiveElements = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'];

    if (interactiveElements.includes(activeElement.tagName)) {
      return false;
    }

    const modalElements = document.querySelectorAll('.modal[aria-modal="true"], [role="dialog"], [aria-modal="true"], dialog[open]');
    for (const modal of modalElements) {
      // Check if modal is actually visible (not hidden)
      if (!modal.classList.contains('hidden') && modal.offsetParent !== null) {
        return false;
      }
    }

    return true;
  }

  updateNextLink() {
    if (!this.isInTriage()) {
      this.hideNextLink();
      this.updatePositionLabel(null);
      return;
    }

    const processes = this.getAllProcesses();
    const currentIndex = processes.findIndex(process => process === this.currentProcessValue);

    // Calculate progress
    const nextIndex = currentIndex + 1;
    const total = processes.length;

    this.updatePositionLabel(currentIndex >= 0 && total > 1 ? `${currentIndex + 1} of ${total}` : null);

    if (nextIndex >= total) {
      this.hideNextLink();
      return;
    }

    const nextProcess = processes[nextIndex];
    this.showNextLink(nextProcess, nextIndex + 1, total);
  }

  updatePositionLabel(text) {
    // Queue position surfaced as a tooltip on the pager arrows, not inline.
    if (this.hasPositionLabelTarget) {
      this.positionLabelTarget.textContent = text || "";
    }
    const suffix = text ? ` · ${text}` : "";
    if (this.hasPrevButtonTarget) this.prevButtonTarget.title = `Previous process${suffix}`;
    if (this.hasNextButtonTarget) this.nextButtonTarget.title = `Next process${suffix}`;
  }

  hideNextLink() {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.href = "#";
      this.nextButtonTarget.setAttribute("aria-label", "Next process");
      this.nextButtonTarget.setAttribute("aria-disabled", "true");
      this.nextButtonTarget.setAttribute("tabindex", "-1");
      this.nextButtonTarget.classList.add("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasNextTooltipTarget) {
      this.nextTooltipTarget.dataset.tip = "No next process";
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.add("hidden");
    }
  }

  showNextLink(nextProcessId, nextNumber, total) {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.href = `/processes/${nextProcessId}`;
      const label = `Next workflow in review queue (${nextNumber}/${total})`;
      this.nextButtonTarget.setAttribute("aria-label", label);
      this.nextButtonTarget.setAttribute("aria-disabled", "false");
      this.nextButtonTarget.removeAttribute("tabindex");
      this.nextButtonTarget.classList.remove("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasNextTooltipTarget) {
      this.nextTooltipTarget.dataset.tip = "Next process";
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.add("hidden");
    }
  }

  clearTriage() {
    localStorage.removeItem('paperworkProcessReferences');
    this.updateNextLink();
    this.updatePrevLink();
  }

  prev(event) {
    if (!this.shouldHandleNavigation()) return;
    
    const processes = this.getAllProcesses();
    if (!processes.length) return;
    
    const currentIndex = processes.findIndex(process => process === this.currentProcessValue);
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex === 0) {
      event?.preventDefault()
      showNavigationEdgeNotice('Start')
      return;
    }
    
    const prevProcess = processes[currentIndex - 1];

    // Show loading state in title
    if (this.hasTitleContainerTarget) {
      this.titleContainerTarget.innerHTML = `<span class="text-base font-normal text-ink-faint">Loading ${prevProcess}...</span>`;
    }

    window.location.href = `/processes/${prevProcess}`;
  }

  next(event) {
    if (!this.shouldHandleNavigation()) return;

    const processes = this.getAllProcesses();
    if (!processes.length) return;

    const currentIndex = processes.findIndex(process => process === this.currentProcessValue);
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex >= processes.length - 1) {
      event?.preventDefault()
      showNavigationEdgeNotice('End')
      return;
    }

    const nextProcess = processes[currentIndex + 1];

    // Show loading state in title
    if (this.hasTitleContainerTarget) {
      this.titleContainerTarget.innerHTML = `<span class="text-base font-normal text-ink-faint">Loading ${nextProcess}...</span>`;
    }

    // Show loading state by swapping button visibility
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.add("hidden");
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.remove("hidden");
    }

    window.location.href = `/processes/${nextProcess}`;
  }

  handleTaskActioned(event) {
    // Only auto-advance if the user has the setting enabled and we're in the review queue
    if (!this.autoAdvanceValue || !this.isInTriage()) {
      return
    }

    // Get the next process
    const processes = this.getAllProcesses()
    if (!processes.length) return

    const currentIndex = processes.findIndex(process => process === this.currentProcessValue)
    if (currentIndex === -1 || currentIndex >= processes.length - 1) {
      return
    }

    const nextProcess = processes[currentIndex + 1]
    this.showAutoAdvanceIndicator()

    // Navigate to the next process after a short delay to allow the UI to update
    setTimeout(() => {
      // Show loading state in title
      if (this.hasTitleContainerTarget) {
        this.titleContainerTarget.innerHTML = `<span class="text-base font-normal text-ink-faint">Loading ${nextProcess}...</span>`
      }

      // Show loading state by swapping button visibility
      if (this.hasNextButtonTarget) {
        this.nextButtonTarget.classList.add("hidden")
      }
      if (this.hasLoadingButtonTarget) {
        this.loadingButtonTarget.classList.remove("hidden")
      }

      window.location.href = `/processes/${nextProcess}`
    }, 500) // Short delay to let the user see the task was actioned
  }

  showAutoAdvanceIndicator() {
    if (!this.hasAutoAdvanceIndicatorTarget) return
    this.autoAdvanceIndicatorTarget.classList.remove("hidden")
  }
}
